import { Box, Typography } from '@mui/material'
import { InfoCircleIcon, PlusIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { housePartsIcons } from '@/assets/icons/house-parts'
import {
  getRetrofitDisplayName,
  retrofitToIcon,
} from '@/features/measures-overview/components/MeasuresTable/MeasuresTable.types'
import EditISFPDataPopup from '@/shared/components/EditISFPDataPopup'
import { MaximumFundingInfoPopup, RenovationCostsInfoPopup } from '@/shared/components/InfoPopup'
import TextButton from '@/shared/components/TextButton'
import type { RetrofitName } from '@/shared/types'
import type { FirestoreCustomPackageDocument } from '@/shared/types/custom-package.types'

interface ProposedMeasuresProps {
  onEmptyStateClick: () => void
  onRetrofitClick: (measureName: RetrofitName) => void
  customPackage: FirestoreCustomPackageDocument | null
}

const ProposedMeasures = ({
  onEmptyStateClick,
  onRetrofitClick,
  customPackage,
}: ProposedMeasuresProps) => {
  const navigate = useNavigate()
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const [isRenovationCostsPopupOpen, setIsRenovationCostsPopupOpen] = useState(false)
  const [isMaxFundingPopupOpen, setIsMaxFundingPopupOpen] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)

  // If there's a custom package, show the measures
  if (customPackage) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '10px',
              }}
            >
              Vorgeschlagene Sanierungsmaßnahmen*
            </Typography>
            <TextButton
              height="28px"
              onClick={() => navigate(`/${buildingUuid}/measures-overview`)}
              sx={{
                fontSize: '11px',
                color: '#181D29',
                paddingX: 2,
                border: '1px solid #181D29',
              }}
            >
              Zur Paketauswahl
            </TextButton>
          </Box>

          <Box
            sx={{
              marginTop: '16px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 150px)',
              gap: '16px',
              justifyContent: 'space-between',
            }}
          >
            {customPackage.measures.map(measureName => (
              <Box
                key={measureName}
                onClick={() => onRetrofitClick(measureName)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  '&:hover .title': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Box
                  component="img"
                  src={housePartsIcons[retrofitToIcon[measureName]]}
                  alt={getRetrofitDisplayName(measureName)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    flexShrink: 0,
                    objectFit: 'contain',
                  }}
                />
                <Typography
                  className="title"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 500,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getRetrofitDisplayName(measureName)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: '8px',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E1E1E4',
              borderRadius: '8px',
              height: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 400,
              }}
            >
              Amortisationszeit
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                marginY: '3px',
              }}
            >
              {(() => {
                const years =
                  customPackage.data.consumptionBasedCalculation.economics.amortizationTimeInYears
                if (!years) return 'k. A.'
                if (years > 40) return '+40 Jahre'
                return `${years} Jahre`
              })()}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E1E1E4',
              borderRadius: '8px',
              height: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '8px',
              position: 'relative',
            }}
          >
            <InfoCircleIcon
              style={{
                width: 18,
                height: 18,
                color: '#464A54',
                position: 'absolute',
                top: '6px',
                right: '8px',
                cursor: 'pointer',
              }}
              onClick={() => setIsRenovationCostsPopupOpen(true)}
            />
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 400,
              }}
            >
              Sanierungskosten
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                marginY: '3px',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '7px',
                  fontWeight: 500,
                }}
              >
                Ab{' '}
              </Typography>
              {Math.round(
                customPackage.data.consumptionBasedCalculation.economics.retrofitCostsInCt.mean /
                  100
              ).toLocaleString('de-DE')}{' '}
              €
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E1E1E4',
              borderRadius: '8px',
              height: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '8px',
              position: 'relative',
            }}
          >
            <InfoCircleIcon
              style={{
                width: 18,
                height: 18,
                color: '#464A54',
                position: 'absolute',
                top: '6px',
                right: '8px',
                cursor: 'pointer',
              }}
              onClick={() => setIsMaxFundingPopupOpen(true)}
            />
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 400,
              }}
            >
              max. Förderung
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                marginY: '3px',
              }}
            >
              {Math.round(
                (customPackage.data.consumptionBasedCalculation.economics.retrofitCostsInCt.mean /
                  100) *
                  (customPackage.data.consumptionBasedCalculation.economics.subsidyRateInPct / 100)
              ).toLocaleString('de-DE')}{' '}
              €
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E1E1E4',
              borderRadius: '8px',
              height: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 400,
              }}
            >
              Kosten nach Förderung
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                marginY: '3px',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '7px',
                  fontWeight: 500,
                }}
              >
                Bis zu{' '}
              </Typography>
              {Math.round(
                (customPackage.data.consumptionBasedCalculation.economics.retrofitCostsInCt.mean /
                  100) *
                  (1 -
                    customPackage.data.consumptionBasedCalculation.economics.subsidyRateInPct / 100)
              ).toLocaleString('de-DE')}{' '}
              €
            </Typography>
          </Box>
        </Box>

        <RenovationCostsInfoPopup
          open={isRenovationCostsPopupOpen}
          onClose={() => setIsRenovationCostsPopupOpen(false)}
          onEdit={() => {
            setIsRenovationCostsPopupOpen(false)
            setIsEditPopupOpen(true)
          }}
        />

        <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />
        <MaximumFundingInfoPopup
          open={isMaxFundingPopupOpen}
          onClose={() => setIsMaxFundingPopupOpen(false)}
        />
      </Box>
    )
  }

  // Default empty state
  return (
    <Box
      onClick={onEmptyStateClick}
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid transparent',
        cursor: 'pointer',
        transition: 'border-color 200ms ease-in-out',
        '&:hover': {
          borderColor: '#E1E1E4',
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '10px',
        }}
      >
        Vorgeschlagene Sanierungsmaßnahmen*
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '16px',
        }}
      >
        <PlusIcon style={{ width: 24, height: 24, color: '#464A54' }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '16px',
        }}
      >
        <Typography
          sx={{
            color: '#464A54',
            fontWeight: 500,
            fontSize: '12px',
            textAlign: 'center',
            maxWidth: '236px',
          }}
        >
          Erstellen Sie ein Paket in der Maßnahmen-Übersicht
        </Typography>
      </Box>
    </Box>
  )
}

export default ProposedMeasures
